import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Form, Input, Button, message, Alert } from "antd";
import "./../style/LoginModal.css";

const Login = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(""); // non-field server errors

  const onFinish = async (values) => {
    setApiError("");
    setLoading(true);

    try {
      // 1) Login
      const res = await fetch("https://biz.dropyourbusiness.com/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: values.email, pass: values.password }),
      });

      // Attempt to parse JSON regardless of ok
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        // If server returns validation errors, set them on fields
        if (data?.errors && typeof data.errors === "object") {
          const fieldErrors = Object.entries(data.errors).map(([name, msgs]) => ({
            name,
            errors: Array.isArray(msgs) ? [msgs[0]] : [String(msgs)],
          }));
          form.setFields(fieldErrors);
        }
        setApiError(data?.message || "Login failed. Please check your credentials.");
        return;
      }

      // Expected success shape:
      // {
      //   "message": "Login successful!",
      //   "user": { "id": 12, "email": "...", "Is_Active": 1, "isListed": true, "businessId": 12, ... }
      // }
      message.success(data?.message || "Login successful!");
      localStorage.setItem("user", JSON.stringify(data));

      // 2) If user has a business, fetch details and store
      const businessId = data?.user?.businessId;
      if (businessId) {
        try {
          const bizRes = await fetch(`https://biz.dropyourbusiness.com/api/business/${businessId}`);
          const bizJson = await bizRes.json().catch(() => ({}));
          if (bizRes.ok && bizJson?.business) {
            const business = bizJson.business;

            // Normalize business_time for easier client usage
            const normalizedBusiness = {
              ...business,
              business_time: {
                ...business.business_time,
                is_24_7: Number(business?.business_time?.is_24_7) === 1,
                working_hours:
                  typeof business?.business_time?.working_hours === "string"
                    ? (() => {
                        try {
                          return JSON.parse(business.business_time.working_hours);
                        } catch {
                          return {};
                        }
                      })()
                    : business?.business_time?.working_hours || {},
              },
            };

            localStorage.setItem("business", JSON.stringify(normalizedBusiness));
            localStorage.setItem("localbusinessId", String(businessId));
          } else if (!bizRes.ok) {
            // Show business fetch error but don't block login
            message.warning(bizJson?.message || "Could not fetch business details.");
          }
        } catch (err) {
          // Non-fatal: user is still logged in
          console.error("Failed to fetch business details:", err);
          message.warning("Could not fetch business details.");
        }
      }

      // 3) Navigate + notify header
      navigate("/");
      window.dispatchEvent(new Event("user-login"));
    } catch (err) {
      console.error(err);
      setApiError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const onFinishFailed = ({ errorFields }) => {
    if (errorFields?.length) {
      message.error("Please fix the highlighted errors.");
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <h2 className="modal-title">
          Log <span className="highlight">In</span>
        </h2>

        {apiError ? (
          <Alert
            type="error"
            message="Login error"
            description={apiError}
            showIcon
            style={{ marginBottom: 16 }}
          />
        ) : null}

        <Form
          form={form}
          name="login-form"
          layout="vertical"
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          requiredMark={false}
          initialValues={{ email: "", password: "" }}
        >
          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: "Email is required." },
              { type: "email", message: "Invalid email format." },
            ]}
          >
            <Input placeholder="Email" />
          </Form.Item>

          <Form.Item
            label="Password"
            name="password"
            rules={[{ required: true, message: "Password is required." }]}
          >
            <Input.Password placeholder="Password" />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              className="login-button"
              loading={loading}
              block
            >
              {loading ? "Logging in..." : "Login"}
            </Button>
          </Form.Item>
        </Form>

        {/* <p className="or-text">Or Login With</p> */}

        <p className="footer-text">
          Haven&apos;t Account? <Link to="/register">Sign Up</Link> &nbsp;&nbsp;|&nbsp;&nbsp;{" "}
          <Link to="/">Forget Password?</Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
